import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { ModalService } from '@purespectrum1/ui/modal';

import { AuthService } from '../../auth/auth.service';
import { MultiBuyerStateService } from '../services/multi-buyer/multi-buyer-state.service';
import { MultiBuyerModalComponent } from '../ui/multi-buyer-modal/multi-buyer-modal.component';
import { MultiBuyerCapabilities } from '../services/multi-buyer/interfaces/multi-buyer-company.interface';

@Injectable({ providedIn: 'root' })
export class MultiBuyerGuard implements CanActivate {
  constructor(
    private readonly _multiBuyerState: MultiBuyerStateService,
    private readonly _modal: ModalService,
    private readonly _auth: AuthService,
    private readonly _router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    if (!this._multiBuyerState.isLoggedInAsMultiBuyer()) {
      return of(true);
    }

    return this._auth.resolveAccessControlConfig().pipe(
      switchMap(() => this._resolveAccess()),
      switchMap((hasAccess) => {
        if (!hasAccess) {
          return of(false);
        }
        return this._resolveCapability(route);
      })
    );
  }

  private _resolveAccess(): Observable<boolean> {
    if (this._multiBuyerState.isSelectedMultiBuyerCompany()) {
      return of(true);
    }

    return this._openSelectionAndWait();
  }

  private _resolveCapability(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> {
    const required = route.data[
      'requiredCapability'
    ] as keyof MultiBuyerCapabilities;

    if (!required) {
      return of(true);
    }

    const { capabilities } = this._multiBuyerState.accessControlConfig;

    return of(
      capabilities[required] ? true : this._router.parseUrl('/access-denied')
    );
  }

  private _openSelectionAndWait(): Observable<boolean> {
    const cmpId =
      this._multiBuyerState.selectedMultiBuyerCompany?.parentCompany ??
      this._auth.userCompanyId;

    const modalRef = this._modal.open(MultiBuyerModalComponent, {
      data: { cmpId },
    });

    return modalRef.onClose$.pipe(
      take(1),
      map(() => this._multiBuyerState.isSelectedMultiBuyerCompany())
    );
  }
}
